import React from "react";
import { DateInput } from "staysure-component-library";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import { StepDef } from "@/app/wizard/wizard-types";
import InputValidationWrapper from "@/components/atoms/input-validation-wrapper/input-validation-wrapper";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { FormDataTypeOrganiser } from "@/types/OrganiserType";
import { smoothScrollToRef } from "@/utils/scrollUtils";
import { isValidDate } from "@/utils/travel-date.utils";

interface OrganiserDOBFieldProps {
  application: string;
  dobStatus: "success" | "error" | "warning" | null;
  errorDetails: { firstError: string | null };
  formData: FormDataTypeOrganiser;
  dobPlaceholder: string | undefined;
  brand: "staysure" | "avanti" | undefined;
  dobMaxLimit: string | undefined;
  dobMinAge: string | undefined;
  dobMaxAge: string | undefined;
  travellerType: string | undefined;
  invalideDate: string;
  calculateAge: () => number | null;
  handleInputChange: (field: string, value: string) => void;
  setInvalideDate: (value: string) => void;
  setFocusedInputs: (fields: string[]) => void;
  getFocusedFields: (field: string) => string[];
  getOrganiserDateValidation: (
    formData: FormDataTypeOrganiser,
    travellerType: string,
    dobMaxLimit: string,
    dobMinAge: string,
    dobMaxAge: string,
    current?: StepDef
  ) => string | null;
  Styles: Record<string, string>;
  dobRef: React.RefObject<HTMLButtonElement | null>;
  firstErrorRef: React.RefObject<HTMLElement | null>;
  lastNameRef: React.RefObject<HTMLElement | null>;
  firstNameRef: React.RefObject<HTMLElement | null>;
  isAvailableNationalityField: boolean;
  childRef: React.MutableRefObject<HTMLDivElement | null> | undefined;
  dateValue: Date | undefined;
  usagePage: string | undefined;
}

export default function OrganiserDOBField({
  application,
  dobStatus,
  formData,
  dobPlaceholder,
  brand,
  dobMaxLimit,
  dobMinAge,
  dobMaxAge,
  travellerType,
  invalideDate,
  calculateAge,
  handleInputChange,
  setInvalideDate,
  setFocusedInputs,
  getFocusedFields,
  getOrganiserDateValidation,
  Styles,
  dobRef,
  lastNameRef,
  firstNameRef,
  isAvailableNationalityField,
  childRef,
  dateValue,
  usagePage,
}: Readonly<OrganiserDOBFieldProps>) {
  const { current } = useWizard();
  const { t } = useWizardTranslation();
  let inputValidationWrapperClassName =
    "sm:!justify-between md:!justify-normal";
  let dateInputDobWrapperExtraClassName = "";
  let dateInputOuterWrapperClassName: string | undefined;
  if (application === "renewal" && usagePage === "organiser") {
    inputValidationWrapperClassName = "sm:!justify-normal w-full";
    dateInputDobWrapperExtraClassName = Styles.dobWidthRenewal;
    dateInputOuterWrapperClassName = Styles.dobOuterWrapperClass;
  }

  return (
    <button
      type="button"
      data-testid="dob-scroll-id"
      tabIndex={-1}
      ref={dobRef}
      onClick={() => {
        const ref = isAvailableNationalityField ? lastNameRef : firstNameRef;
        smoothScrollToRef(ref, 100);
      }}
      className={`${Styles.date_picker_wrapper}  flex flex-col relative w-full focus-visible:outline-none`}
    >
      <InputValidationWrapper
        className={`${Styles.date_picker_wrapper} flex flex-row relative mb-spacing-s ${inputValidationWrapperClassName}`}
        status={dobStatus}
        childRef={childRef ?? undefined}
        isDateInput
      >
        <DateInput
          isDisabled={application === "renewal"}
          isDOBScrollable={false}
          defaultDate={
            dateValue && isValidDate(dateValue)
              ? new Date(dateValue)
              : undefined
          }
          hintLabel={dobPlaceholder}
          brand={brand}
          customizedMessage={
            getOrganiserDateValidation(
              formData,
              travellerType ?? "",
              dobMaxLimit ?? "",
              dobMinAge ?? "",
              dobMaxAge ?? "",
              current
            ) ?? invalideDate
          }
          dobErrorMsgClass={`${Styles.errorOfDob} !max-w-[170px] sm:!max-w-[200px] !whitespace-normal !break-words`}
          isError={dobStatus === "error"}
          dobWrapperClass={`${Styles.dobWrapper} ${
            dobStatus === "warning" ? Styles.warningMsgClass : ""
          } ${
            dobStatus === "success" && application !== "renewal"
              ? Styles.successMsgClass
              : ""
          } ${
            dobStatus === "error" ? Styles.errorMsgClass : ""
          } !max-w-[170px] sm:!max-w-[200px] !flex-shrink-0
          ${
            application === "renewal"
              ? "[&>div>input]:text-text-disabled !bg-surface-neutral-option-2 [&>div]:!bg-surface-neutral-option-2 [&>div]:text-text-disabled"
              : ""
          } ${dateInputDobWrapperExtraClassName}`}
          outerWraperClass={`${dateInputOuterWrapperClassName} !max-w-[170px] sm:!max-w-[200px]`}
          onChange={(date: unknown) => {
            if (isValidDate(date as Date)) {
              handleInputChange("dateValue", date?.toString() ?? "");
              setInvalideDate("");
            } else {
              handleInputChange("dateValue", "Invalidate Date of Birth");
              setInvalideDate("Invalidate Date of Birth");
            }
          }}
          onBlur={() => setFocusedInputs(getFocusedFields("dateValue"))}
          onDateComplete={(date: string) => {
            if (date.length > 0 && date.length < 8) {
              handleInputChange("dateValue", "");
              setInvalideDate("");
            }
          }}
        />

        <div
          className={`${
            application === "renewal"
              ? "text-text-disabled"
              : "text-text-on-primary"
          } 
                  text-xs leading-[27px] font-semibold text-text-on-primary 
                      ml-3 md:ml-4 mt-4 min-w-[65px] sm:relative sm:right-0 whitespace-nowrap`}
        >
          {`${t("age", "Age")}: ${calculateAge() ?? ""}`}
        </div>
      </InputValidationWrapper>
    </button>
  );
}
